import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';

const ReportBuilder = () => {
  const [selectedWidgets, setSelectedWidgets] = useState(['email-performance', 'campaign-comparison']);
  const [reportName, setReportName] = useState('');
  const [scheduleFrequency, setScheduleFrequency] = useState('weekly');

  const availableWidgets = [
    {
      id: 'email-performance',
      name: 'Email Performance',
      description: 'Open rates, click-through rates, and bounce metrics',
      icon: 'Mail',
      category: 'Email Analytics'
    },
    {
      id: 'campaign-comparison',
      name: 'Campaign Comparison',
      description: 'Side-by-side performance analysis',
      icon: 'BarChart3',
      category: 'Campaign Analytics'
    },
    {
      id: 'media-mentions',
      name: 'Media Mentions',
      description: 'Tracking and sentiment analysis',
      icon: 'MessageSquare',
      category: 'Media Monitoring'
    },
    {
      id: 'contact-engagement',
      name: 'Contact Engagement',
      description: 'Individual contact interaction patterns',
      icon: 'Users',
      category: 'Contact Analytics'
    },
    {
      id: 'press-kit-analytics',
      name: 'Press Kit Analytics',
      description: 'Download and view statistics',
      icon: 'FileText',
      category: 'Content Analytics'
    },
    {
      id: 'geographic-distribution',
      name: 'Geographic Distribution',
      description: 'Regional engagement breakdown',
      icon: 'Globe',
      category: 'Geographic Analytics'
    }
  ];

  const savedReports = [
    {
      id: 1,
      name: 'Weekly Executive Summary',
      widgets: 4,
      schedule: 'Weekly',
      lastGenerated: new Date(Date.now() - 86400000),
      recipients: 3
    },
    {
      id: 2,
      name: 'Campaign Performance Deep Dive',
      widgets: 6,
      schedule: 'Monthly',
      lastGenerated: new Date(Date.now() - 604800000),
      recipients: 5
    },
    {
      id: 3,
      name: 'Media Monitoring Report',
      widgets: 3,
      schedule: 'Daily',
      lastGenerated: new Date(Date.now() - 3600000),
      recipients: 2
    }
  ];

  const toggleWidget = (widgetId) => {
    setSelectedWidgets(prev => 
      prev?.includes(widgetId) 
        ? prev?.filter(id => id !== widgetId)
        : [...prev, widgetId]
    );
  };

  const formatDate = (date) => {
    return date?.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric'
    });
  };

  return (
    <div className="bg-card border border-border rounded-lg shadow-sm">
      <div className="p-6 border-b border-border">
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-3">
            <div className="p-2 bg-primary/10 rounded-lg">
              <Icon name="FileText" size={20} className="text-primary" />
            </div>
            <div>
              <h3 className="text-lg font-semibold text-foreground">Report Builder</h3>
              <p className="text-sm text-muted-foreground">Create custom analytics reports</p>
            </div>
          </div>
          
          <Button variant="default" iconName="Plus" iconPosition="left">
            New Report
          </Button>
        </div>
      </div>
      <div className="p-6">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
          {/* Report Configuration */}
          <div className="space-y-6">
            <div>
              <h4 className="text-sm font-medium text-foreground mb-4">Report Configuration</h4>
              
              <div className="space-y-4">
                <Input
                  label="Report Name"
                  type="text"
                  placeholder="Enter report name"
                  value={reportName}
                  onChange={(e) => setReportName(e?.target?.value)}
                />
                
                <div>
                  <label className="block text-sm font-medium text-foreground mb-2">
                    Schedule Frequency
                  </label>
                  <div className="grid grid-cols-3 gap-2">
                    {['daily', 'weekly', 'monthly']?.map((freq) => (
                      <Button
                        key={freq}
                        variant={scheduleFrequency === freq ? 'default' : 'outline'}
                        size="sm"
                        onClick={() => setScheduleFrequency(freq)}
                      >
                        {freq?.charAt(0)?.toUpperCase() + freq?.slice(1)}
                      </Button>
                    ))}
                  </div>
                </div>
              </div>
            </div>
            
            <div>
              <h4 className="text-sm font-medium text-foreground mb-4">Select Widgets</h4>
              <div className="space-y-3 max-h-80 overflow-y-auto">
                {availableWidgets?.map((widget) => (
                  <div
                    key={widget?.id}
                    className={`p-4 border rounded-lg cursor-pointer transition-all duration-200 ${
                      selectedWidgets?.includes(widget?.id)
                        ? 'border-primary bg-primary/5' :'border-border hover:border-primary/50'
                    }`}
                    onClick={() => toggleWidget(widget?.id)}
                  >
                    <div className="flex items-start space-x-3">
                      <div className={`p-2 rounded-lg ${
                        selectedWidgets?.includes(widget?.id) ? 'bg-primary text-white' : 'bg-muted'
                      }`}>
                        <Icon name={widget?.icon} size={16} />
                      </div>
                      
                      <div className="flex-1">
                        <div className="flex items-center justify-between">
                          <h5 className="text-sm font-medium text-foreground">{widget?.name}</h5>
                          {selectedWidgets?.includes(widget?.id) && (
                            <Icon name="Check" size={16} className="text-primary" />
                          )}
                        </div>
                        <p className="text-xs text-muted-foreground mt-1">{widget?.description}</p>
                        <span className="text-xs text-primary font-medium">{widget?.category}</span>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
            
            <div className="flex space-x-3">
              <Button variant="default" className="flex-1">
                Save Report
              </Button>
              <Button variant="outline" iconName="Eye">
                Preview
              </Button>
            </div>
          </div>
          
          {/* Saved Reports */}
          <div>
            <h4 className="text-sm font-medium text-foreground mb-4">Saved Reports</h4>
            <div className="space-y-4">
              {savedReports?.map((report) => (
                <div key={report?.id} className="p-4 border border-border rounded-lg">
                  <div className="flex items-center justify-between mb-3">
                    <h5 className="text-sm font-medium text-foreground">{report?.name}</h5>
                    <div className="flex items-center space-x-2">
                      <Button variant="ghost" size="sm" iconName="Edit2">
                        Edit
                      </Button>
                      <Button variant="ghost" size="sm" iconName="Download">
                        Export
                      </Button>
                    </div>
                  </div>
                  
                  <div className="grid grid-cols-2 gap-4 text-xs text-muted-foreground">
                    <div>
                      <span className="block">Widgets: {report?.widgets}</span>
                      <span className="block">Schedule: {report?.schedule}</span>
                    </div>
                    <div>
                      <span className="block">Recipients: {report?.recipients}</span>
                      <span className="block">Last: {formatDate(report?.lastGenerated)}</span>
                    </div>
                  </div>
                  
                  <div className="mt-3 pt-3 border-t border-border">
                    <div className="flex items-center space-x-3">
                      <Button variant="outline" size="sm" className="flex-1">
                        Generate Now
                      </Button>
                      <Button variant="ghost" size="sm" iconName="Send">
                        Send
                      </Button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
            
            <div className="mt-6 p-4 bg-muted/30 rounded-lg">
              <div className="flex items-center space-x-3">
                <Icon name="Clock" size={16} className="text-muted-foreground" />
                <div>
                  <p className="text-sm font-medium text-foreground">Automated Delivery</p>
                  <p className="text-xs text-muted-foreground">Reports are automatically generated and sent based on schedule</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ReportBuilder;